import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customers } from '../services/api';
import LayoutV2 from '../components/LayoutV2';
import {
  Badge,
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../components/ui';

function getOrderStatusVariant(status) {
  const colors = {
    pass: 'success',
    reject: 'danger',
    pending: 'warning'
  };
  return colors[status] || 'gray';
}

function getCustomerStatusVariant(statusCode) {
  const map = {
    closed_deal: 'success',
    rejected: 'danger',
    applied: 'blue',
    kyc_submitted: 'primary',
    registered: 'gray',
    invalid: 'danger',
    unregistered: 'warning'
  };
  return map[statusCode] || 'gray';
}

function formatDateTime(value) {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatCurrency(value) {
  return `₱${Number(value || 0).toLocaleString()}`;
}

export default function CustomerDetailV2() {
  const { mobile } = useParams();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const isAdminView = location.pathname.startsWith('/admin/customers');
  const backTo = isAdminView ? '/admin/customers' : '/customers';
  const showMerchantColumn = isAdmin;
  const [customer, setCustomer] = useState(null);
  const [preapprovals, setPreapprovals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('preapprovals');

  useEffect(() => {
    const loadCustomerData = async () => {
      setLoading(true);
      try {
        const [profileRes, preRes, orderRes] = await Promise.all([
          customers.getProfile(mobile),
          customers.getPreapprovals(mobile),
          customers.getOrders(mobile)
        ]);

        const nextCustomer = profileRes.data.customer || { mobile, user_name: null };
        setCustomer({
          ...nextCustomer,
          inviteCode: profileRes.data.inviteCode || null,
          promoter: profileRes.data.promoter || null
        });
        setPreapprovals(preRes.data.preapprovals || []);
        setOrders(orderRes.data.orders || []);
      } catch (err) {
        console.error('Failed to load customer data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerData();
  }, [mobile]);

  if (loading) {
    return (
      <LayoutV2>
        <Loading text="Loading customer details..." />
      </LayoutV2>
    );
  }

  return (
    <LayoutV2>
      <PageHeader
        title={customer?.user_name ? `${customer.user_name}` : mobile}
        actions={
          <Link
            to={backTo}
            state={!isAdminView ? location.state : undefined}
            className="btn btn-secondary"
          >
            Back
          </Link>
        }
      />

      <div className="space-y-4">
        <SectionBlock
          title="Summary"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
              <p className="label">Mobile</p>
              <p className="text-sm text-slate-100">{mobile}</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
              <p className="label">Promoter</p>
              <p className="text-sm text-slate-100">
                {customer?.promoter?.displayName || '--'}
                {customer?.promoter?.username ? ` (@${customer.promoter.username})` : ''}
              </p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
              <p className="label">Referral code</p>
              <p className="font-mono text-sm text-slate-100">{customer?.inviteCode || '--'}</p>
            </div>
            <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
              <p className="label">Customer status</p>
              <div className="mt-1.5">
                <Badge variant={getCustomerStatusVariant(customer?.customerStatus)}>
                  {customer?.customerStatusLabel || '--'}
                </Badge>
              </div>
            </div>
          </div>
        </SectionBlock>

        <SectionBlock
          title="Records"
          actions={
            <div className="flex gap-2">
              <Button
                type="button"
                variant={activeTab === 'preapprovals' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('preapprovals')}
              >
                Pre-approvals ({preapprovals.length})
              </Button>
              <Button
                type="button"
                variant={activeTab === 'orders' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('orders')}
              >
                Loan Orders ({orders.length})
              </Button>
            </div>
          }
        >
          {activeTab === 'preapprovals' ? (
            preapprovals.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/10 bg-slate-950/20 p-4 text-sm text-slate-400">
                No pre-approval records were returned for this customer.
              </div>
            ) : (
              <>
                <div className="hidden lg:block table-container">
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                         <tr>
                           <th>Product</th>
                           <th>Status</th>
                           <th>Credit Line</th>
                           <th>Pre-approve Time</th>
                           <th>Expire Time</th>
                         </tr>
                      </thead>
                      <tbody>
                        {preapprovals.map((item) => (
                           <tr key={item.id}>
                             <td>{item.loan_name || `Partner #${item.partner_id}`}</td>
                             <td>
                               <span className={`px-2 py-1 rounded text-xs font-medium ${
                                 item.statusDisplay === 'Approved' ? 'bg-green-900/30 text-green-300' :
                                 item.statusDisplay === 'Rejected' ? 'bg-red-900/30 text-red-300' :
                                 'bg-slate-800 text-slate-400'
                               }`}>
                                 {item.statusDisplay}
                               </span>
                             </td>
                             <td className="font-semibold text-emerald-200">{formatCurrency(item.credit_line)}</td>
                             <td>{formatDateTime(item.preapprove_time)}</td>
                             <td>{formatDateTime(item.expire_time)}</td>
                           </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2.5 lg:hidden">
                  {preapprovals.map((item) => (
                     <div key={item.id} className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
                       <div className="flex items-center justify-between">
                         <p className="text-sm font-semibold text-slate-100">{item.loan_name || `Partner #${item.partner_id}`}</p>
                         <span className={`px-2 py-1 rounded text-xs font-medium ${
                           item.statusDisplay === 'Approved' ? 'bg-green-900/30 text-green-300' :
                           item.statusDisplay === 'Rejected' ? 'bg-red-900/30 text-red-300' :
                           'bg-slate-800 text-slate-400'
                         }`}>
                           {item.statusDisplay}
                         </span>
                       </div>
                       <p className="mt-1.5 text-sm font-semibold text-emerald-200">{formatCurrency(item.credit_line)}</p>
                       <p className="mt-2.5 text-xs text-slate-500">Pre-approve Time</p>
                       <p className="text-sm text-slate-300">{formatDateTime(item.preapprove_time)}</p>
                       <p className="mt-2.5 text-xs text-slate-500">Expire Time</p>
                       <p className="text-sm text-slate-300">{formatDateTime(item.expire_time)}</p>
                     </div>
                  ))}
                </div>
              </>
            )
          ) : orders.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 bg-slate-950/20 p-4 text-sm text-slate-400">
              No loan-order records were returned for this customer.
            </div>
          ) : (
            <>
              <div className="hidden lg:block table-container">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        {showMerchantColumn ? <th>Merchant</th> : null}
                        <th>Product</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Apply Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((item) => (
                        <tr key={item.id}>
                          {showMerchantColumn ? <td>{item.merchant_name}</td> : null}
                          <td>{item.loan_name}</td>
                          <td className="font-semibold text-emerald-200">{formatCurrency(item.audit_amount)}</td>
                          <td>
                            <Badge variant={getOrderStatusVariant(item.audit_status)}>
                              {item.audit_status === 'pass' ? 'Approved' : item.audit_status === 'reject' ? 'Rejected' : item.audit_status}
                            </Badge>
                          </td>
                          <td>{formatDateTime(item.apply_time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2.5 lg:hidden">
                {orders.map((item) => (
                  <div key={item.id} className="rounded-xl border border-white/8 bg-white/[0.03] p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-100">{item.loan_name}</p>
                        {showMerchantColumn ? <p className="mt-1 text-sm text-slate-400">{item.merchant_name}</p> : null}
                      </div>
                      <Badge variant={getOrderStatusVariant(item.audit_status)}>
                        {item.audit_status === 'pass' ? 'Approved' : item.audit_status === 'reject' ? 'Rejected' : item.audit_status}
                      </Badge>
                    </div>
                    <p className="mt-2.5 text-sm font-semibold text-emerald-200">{formatCurrency(item.audit_amount)}</p>
                    <p className="mt-2.5 text-xs text-slate-500">Apply Time</p>
                    <p className="text-sm text-slate-300">{formatDateTime(item.apply_time)}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionBlock>
      </div>
    </LayoutV2>
  );
}
